import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI, jobsAPI, notificationsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Calendar,
  Users,
  Briefcase,
  Building2,
  Clock,
  Bell,
  ChevronRight,
  MapPin,
  Filter,
  Activity,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { DashboardJobSearch } from '../components/DashboardJobSearch';

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [upcomingJobs, setUpcomingJobs] = useState([]);
  const [activeJobs, setActiveJobs] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Cost Center filter
  const [costCenters, setCostCenters] = useState([]);
  const [selectedCostCenter, setSelectedCostCenter] = useState('all');

  const isAdmin = user?.role === 'admin';

  // Fetch cost centers on mount
  useEffect(() => {
    const fetchCostCenters = async () => {
      try {
        const res = await dashboardAPI.getCostCenters();
        setCostCenters(res.data.cost_centers || []);
      } catch (error) {
        console.error('Failed to fetch cost centers:', error);
      }
    };
    fetchCostCenters();
  }, []);

  // Fetch data when cost center changes
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const costCenterParam = selectedCostCenter === 'all' ? null : selectedCostCenter;
        const [statsRes, jobsRes, activeRes, notifRes] = await Promise.all([
          dashboardAPI.getStats(costCenterParam),
          jobsAPI.getAll({ status: 'pending' }),
          jobsAPI.getAll({ status: 'in_progress', page_size: 20 }),
          notificationsAPI.getAll(),
        ]);
        setStats(statsRes.data);
        // Filter upcoming jobs by cost center if selected
        let jobs = Array.isArray(jobsRes.data) ? jobsRes.data : (jobsRes.data.items || jobsRes.data || []);
        if (costCenterParam) {
          jobs = jobs.filter(j => j.cost_center === costCenterParam);
        }
        setUpcomingJobs(jobs.slice(0, 5));
        // Active jobs (in_progress) - filter by cost center if selected
        let active = Array.isArray(activeRes.data) ? activeRes.data : (activeRes.data.items || activeRes.data || []);
        if (costCenterParam) {
          active = active.filter(j => j.cost_center === costCenterParam);
        }
        setActiveJobs(active);
        const notifs = notifRes.data.items || notifRes.data || [];
        setNotifications(notifs.slice(0, 5));
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAdmin, selectedCostCenter]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'in_progress': return 'status-in_progress';
      case 'completed': return 'status-completed';
      case 'cancelled': return 'status-cancelled';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const adminStats = [
    { label: 'Total Employees', value: stats?.total_employees, icon: Users, color: 'text-blue-600', link: '/employees' },
    { label: 'Total Jobs', value: stats?.total_jobs, icon: Briefcase, color: 'text-purple-600', link: '/jobs' },
    { label: 'Total Customers', value: stats?.total_customers, icon: Building2, color: 'text-orange-600', link: '/customers' },
  ];

  const employeeStats = [
    { label: 'My Jobs', value: stats?.my_jobs, icon: Briefcase, color: 'text-blue-600', link: '/jobs' },
  ];

  const displayStats = isAdmin ? adminStats : employeeStats;

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Skeleton className="h-10 w-64" />
        <div className="dashboard-grid">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in" data-testid="dashboard">
      {/* Header with Cost Center Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">
            Here is what is happening with your team today.
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <DashboardJobSearch />
          {/* Cost Center Filter */}
          {isAdmin && costCenters.length > 0 && (
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={selectedCostCenter} onValueChange={setSelectedCostCenter}>
                <SelectTrigger className="w-48 bg-slate-700 text-white border-slate-600" data-testid="dashboard-cost-center-filter">
                  <SelectValue placeholder="All Divisions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Divisions</SelectItem>
                  {costCenters.map((cc) => (
                    <SelectItem key={cc} value={cc}>
                      {cc}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="dashboard-grid">
        {displayStats.map((stat) => (
          <Link key={stat.label} to={stat.link}>
            <Card className="card-interactive cursor-pointer hover:shadow-lg transition-shadow" data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-muted-foreground truncate">{stat.label}</p>
                    <p className="text-2xl sm:text-3xl font-bold font-heading mt-1">{stat.value ?? 0}</p>
                  </div>
                  <div className={`p-2 sm:p-3 rounded-full bg-muted ${stat.color} flex-shrink-0 ml-2`}>
                    <stat.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Active Jobs (in progress) - admin sees all, employee sees own */}
      <Card data-testid="active-jobs-card">
        <CardHeader className="flex flex-row items-center justify-between pb-4 px-4 sm:px-6">
          <CardTitle className="font-heading text-lg sm:text-xl flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
            </span>
            Active Jobs
            <span className="text-sm font-normal text-muted-foreground">({activeJobs.length})</span>
          </CardTitle>
          <Button variant="ghost" size="sm" asChild className="text-xs sm:text-sm">
            <Link to="/jobs?status=in_progress">
              View all <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent className="px-4 sm:px-6">
          {activeJobs.length === 0 ? (
            <p className="text-muted-foreground text-center py-6 sm:py-8 text-sm">No active jobs right now</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
              {activeJobs.map((job) => (
                <Link
                  key={job.id}
                  to={`/jobs/${job.id}`}
                  className="flex items-start gap-3 p-3 sm:p-4 rounded-lg border border-emerald-500/20 bg-emerald-500/5 hover:bg-emerald-500/10 transition-colors"
                  data-testid={`active-job-${job.id}`}
                >
                  <div className="p-2 rounded-lg bg-emerald-500/15 text-emerald-600 flex-shrink-0">
                    <Activity className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate text-sm sm:text-base" data-testid={`active-job-title-${job.id}`}>
                      {job.title}
                    </p>
                    {job.customer_name && (
                      <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 truncate">
                        {job.customer_name}
                      </p>
                    )}
                    {job.location && (
                      <div className="flex items-center gap-1.5 mt-1 text-xs sm:text-sm text-muted-foreground">
                        <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                        <span className="truncate">{job.location}</span>
                      </div>
                    )}
                    <Badge className={`badge-status ${getStatusColor(job.status)} text-[10px] sm:text-xs mt-2`}>
                      {job.status.replace('_', ' ')}
                    </Badge>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Two column layout - Jobs and Notifications */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Upcoming Jobs */}
        <Card data-testid="upcoming-jobs-card">
          <CardHeader className="flex flex-row items-center justify-between pb-4 px-4 sm:px-6">
            <CardTitle className="font-heading text-lg sm:text-xl">Upcoming Jobs</CardTitle>
            <Button variant="ghost" size="sm" asChild className="text-xs sm:text-sm">
              <Link to="/jobs">
                View all <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4 px-4 sm:px-6">
            {upcomingJobs.length === 0 ? (
              <p className="text-muted-foreground text-center py-6 sm:py-8 text-sm">No upcoming jobs</p>
            ) : (
              upcomingJobs.map((job) => (
                <Link
                  key={job.id}
                  to={`/jobs/${job.id}`}
                  className="flex items-start gap-3 sm:gap-4 p-2 sm:p-3 rounded-lg hover:bg-accent transition-colors"
                  data-testid={`job-${job.id}`}
                >
                  <div className="p-1.5 sm:p-2 rounded-lg bg-primary/10 text-primary flex-shrink-0">
                    <Briefcase className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate text-sm sm:text-base">{job.title}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs sm:text-sm text-muted-foreground">
                      <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0 text-muted-foreground" />
                      <span className="truncate">{format(parseISO(job.start_time), 'MMM d, h:mm a')}</span>
                    </div>
                    {job.location && (
                      <div className="flex items-center gap-2 mt-0.5 text-xs sm:text-sm text-muted-foreground">
                        <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                        <span className="truncate">{job.location}</span>
                      </div>
                    )}
                  </div>
                  <Badge className={`badge-status ${getStatusColor(job.status)} text-[10px] sm:text-xs flex-shrink-0`}>
                    {job.status.replace('_', ' ')}
                  </Badge>
                </Link>
              ))
            )}
          </CardContent>
        </Card>

        {/* Recent Notifications */}
        <Card data-testid="notifications-card">
          <CardHeader className="flex flex-row items-center justify-between pb-4 px-4 sm:px-6">
            <CardTitle className="font-heading text-lg sm:text-xl">Recent Notifications</CardTitle>
            <Button variant="ghost" size="sm" asChild className="text-xs sm:text-sm">
              <Link to="/notifications">
                View all <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4 px-4 sm:px-6">
            {notifications.length === 0 ? (
              <p className="text-muted-foreground text-center py-6 sm:py-8 text-sm">No notifications</p>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  className={`flex items-start gap-3 sm:gap-4 p-2 sm:p-3 rounded-lg ${
                    !notif.is_read ? 'bg-primary/5' : ''
                  }`}
                  data-testid={`notification-${notif.id}`}
                >
                  <div className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 ${
                    notif.type === 'job_assignment' ? 'bg-blue-100 text-blue-600' :
                    notif.type === 'alert' ? 'bg-red-100 text-red-600' :
                    notif.type === 'warning' ? 'bg-yellow-100 text-yellow-600' :
                    notif.type === 'clock_in' ? 'bg-green-100 text-green-600' :
                    notif.type === 'clock_out' ? 'bg-orange-100 text-orange-600' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    <Bell className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm sm:text-base">{notif.title}</p>
                    <p className="text-xs sm:text-sm text-muted-foreground truncate">{notif.message}</p>
                    <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                      {format(parseISO(notif.created_at), 'MMM d, h:mm a')}
                    </p>
                  </div>
                  {!notif.is_read && (
                    <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                  )}
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
